import { c as _c } from "react/compiler-runtime";
import { ChevronRight, Info } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { SIDEBAR_HEADING_CLASS, SIDEBAR_HEADING_VALUE_CLASS } from '../../shared/sidebar-heading-classes';
import { AiAttributionBreakdownBar } from './ai-attribution-breakdown-bar';
import { AiAttributionDetectedAgentsList } from './ai-attribution-detected-agents-list';
import { buildAiAttributionLegendConfig, buildAiAttributionLegendPayload, buildAiAttributionSegments } from './ai-attribution-segments';
import { AiAttributionToolsList } from './ai-attribution-tools-list';
import { BetterTooltip } from '@/components/ui/tooltip';
import { usePrV2AiAttribution } from '@/data/v2/pr/use-pr-v2-ai-attribution';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
const AI_CODE_TOOLTIP = <>
    Percentage of AI-attributed lines in this PR.
    <br />
    Includes pure AI code and AI-generated code that was later edited by humans.
  </>;
const AI_AGENTS_TOOLTIP = 'Detected from commit and PR metadata left by AI coding agents. Detection accuracy varies by agent, so this is an estimate.';
type AiAttributionProps = {
  owner: string;
  repo: string;
  number: string;
  headSha: string | null;
  installationId: number | null;
};
function AiAttributionInfoIcon(t0) {
  const $ = _c(3);
  const {
    tooltip
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="inline-flex h-4 w-4 items-center justify-center text-zinc-500"><Info className="size-4" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== tooltip) {
    t2 = <BetterTooltip content={tooltip} side="top">{t1}</BetterTooltip>;
    $[1] = tooltip;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function AiAttribution(t0) {
  const $ = _c(38);
  const {
    owner,
    repo,
    number,
    headSha,
    installationId
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const aiAttributionQuery = usePrV2AiAttribution(owner, repo, number, headSha);
  const aiAttribution = aiAttributionQuery.data;
  const showValue = aiAttribution?.available === true && aiAttribution.aiAttributedRate !== null;
  if (!aiAttribution || !showValue && aiAttribution.detectedAgents.length === 0) {
    return null;
  }
  if (!showValue) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div data-testid="pr-v2-ai-agents-header" className="mb-2 flex w-full items-center gap-1.5"><span className={SIDEBAR_HEADING_CLASS}>AI agents</span><AiAttributionInfoIcon tooltip={AI_AGENTS_TOOLTIP} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== aiAttribution.detectedAgents) {
      t2 = <section data-testid="pr-v2-ai-code-section" className="mt-6 w-full md:w-[220px]">{t1}<AiAttributionDetectedAgentsList agents={aiAttribution.detectedAgents} testId="pr-v2-ai-agents-list" /></section>;
      $[1] = aiAttribution.detectedAgents;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let legendConfig;
  let segments;
  let t1;
  if ($[3] !== aiAttribution) {
    segments = buildAiAttributionSegments(aiAttribution);
    legendConfig = buildAiAttributionLegendConfig(segments);
    t1 = buildAiAttributionLegendPayload(segments);
    $[3] = aiAttribution;
    $[4] = legendConfig;
    $[5] = segments;
    $[6] = t1;
  } else {
    legendConfig = $[4];
    segments = $[5];
    t1 = $[6];
  }
  const legendPayload = t1;
  let t2;
  if ($[7] !== installationId || $[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = () => {
      setExpanded(current => {
        if (!current) {
          trackClientEvent("v2_pr_ai_code_expanded", installationId?.toString(), {
            owner,
            repo,
            pr_number: number
          });
        }
        return !current;
      });
    };
    $[7] = installationId;
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = expanded ? "Collapse AI code breakdown" : "Expand AI code breakdown";
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className={SIDEBAR_HEADING_CLASS}>AI code</span>;
    t5 = <AiAttributionInfoIcon tooltip={AI_CODE_TOOLTIP} />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  const t6 = `size-4 text-zinc-500 opacity-0 transition-all group-hover/ai-code:opacity-100 ${expanded ? "rotate-90" : ""}`;
  let t7;
  if ($[14] !== t6) {
    t7 = <span className="flex items-center gap-1.5">{t4}{t5}<ChevronRight className={t6} /></span>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `${aiAttribution.aiAttributedRate}%`;
  let t9;
  if ($[16] !== t8) {
    t9 = <span className={SIDEBAR_HEADING_VALUE_CLASS} data-testid="pr-v2-ai-code-percent">{t8}</span>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== expanded || $[19] !== t2 || $[20] !== t3 || $[21] !== t7 || $[22] !== t9) {
    t10 = <button type="button" data-testid="pr-v2-ai-code-header" className="group/ai-code mb-2 flex w-full items-center justify-between gap-3" onClick={t2} aria-expanded={expanded} aria-label={t3}>{t7}{t9}</button>;
    $[18] = expanded;
    $[19] = t2;
    $[20] = t3;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== legendConfig || $[25] !== legendPayload || $[26] !== segments) {
    t11 = <AiAttributionBreakdownBar segments={segments} legendConfig={legendConfig} legendPayload={legendPayload} />;
    $[24] = legendConfig;
    $[25] = legendPayload;
    $[26] = segments;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== aiAttribution.detectedAgents || $[29] !== aiAttribution.tools || $[30] !== expanded) {
    t12 = expanded ? <AiAttributionToolsList tools={aiAttribution.tools} detectedAgents={aiAttribution.detectedAgents} /> : null;
    $[28] = aiAttribution.detectedAgents;
    $[29] = aiAttribution.tools;
    $[30] = expanded;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <div className="space-y-3">{t11}{t12}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t10 || $[36] !== t13) {
    t14 = <section data-testid="pr-v2-ai-code-section" className="mt-6 w-full md:w-[220px]">{t10}{t13}</section>;
    $[35] = t10;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}